// @ts-nocheck
import axios from "@/helpers/interceptors";
import { getEntityId } from "@/helpers/utils";
import { B2bPortalRight } from "@/enums/B2bPortalRight";

/**
 * Mirrors `UserManagementController` RequirePermission:
 * GET /api/users, GET /api/users/roles, PUT /api/users/{id}, DELETE /api/users/{id} → UsersView
 * POST /api/users → UsersAdd
 */
function portalRights(rootState) {
  const r = rootState?.auth?.permissions?.rights;
  return Array.isArray(r) ? r : null;
}

function hasPortalRight(rootState, right) {
  return !!portalRights(rootState)?.includes(right);
}

function denyUnlessPortalRight(rootState, right, operation) {
  if (!hasPortalRight(rootState, right)) {
    throw new Error(`Permission denied: ${operation} requires ${right}`);
  }
}

/** User list item: assignment Id + RoleId + Name (see UserManagementController GetUsersList). */
function mapUserPortalRoleFromApi(r) {
  if (r == null) return null;
  const roleId = Number(r.roleId ?? r.RoleId ?? 0);
  if (!Number.isFinite(roleId) || roleId === 0) return null;
  const id = Number(r.id ?? r.Id ?? 0);
  const name = r.name ?? r.Name ?? "";
  return { id: Number.isFinite(id) ? id : 0, roleId, name };
}

/** Roles catalog: RoleId may come from roleId or id (AutoMapper from B2bPortalRoleDto). */
function mapCatalogPortalRoleFromApi(r) {
  if (r == null) return null;
  const roleId = Number(r.roleId ?? r.RoleId ?? r.id ?? r.Id ?? 0);
  if (!Number.isFinite(roleId) || roleId === 0) return null;
  const id = Number(r.id ?? r.Id ?? 0);
  const name = r.name ?? r.Name ?? "";
  return { roleId, name, id: Number.isFinite(id) ? id : 0 };
}

/** Builds request body for B2bPortalRoles: assignment Id + RoleId per controller. */
function buildB2bPortalRolesForRequest(roleIds, roleAssignmentIds = {}) {
  const ids = roleAssignmentIds || {};
  return (Array.isArray(roleIds) ? roleIds : [])
    .map((x) => Number(x))
    .filter((roleId) => Number.isFinite(roleId))
    .map((roleId) => ({
      id: Number(ids[roleId] ?? ids[String(roleId)] ?? 0) || 0,
      roleId,
    }));
}

const state = {
  users: [],
  roles: [],
  isLoading: true,
};

const mutations = {
  SET_USERS(state, users) {
    state.users = Array.isArray(users) ? users : [];
  },
  SET_LOADING(state, isLoading) {
    state.isLoading = !!isLoading;
  },
  ADD_USER(state, user) {
    state.users = [user, ...(state.users || [])];
  },
  DELETE_USER(state, userId) {
    state.users = (state.users || []).filter((u) => u.id !== userId);
  },
  UPDATE_USER(state, user) {
    const list = state.users || [];
    const id = user?.id;
    const idx = list.findIndex((u) => u.id === id);
    if (idx < 0) return;
    const next = { ...list[idx], ...user };
    state.users = [...list.slice(0, idx), next, ...list.slice(idx + 1)];
  },
  SET_ROLES(state, roles) {
    state.roles = Array.isArray(roles) ? roles : [];
  },
};

const actions = {
  async fetchRoles({ commit, rootState }) {
    const legalEntityId = getEntityId();
    if (!legalEntityId) {
      commit('SET_ROLES', []);
      return [];
    }
    if (!hasPortalRight(rootState, B2bPortalRight.UsersView)) {
      commit('SET_ROLES', []);
      return [];
    }
    try {
      const res = await axios.get(`/api/users/roles`);
      const data = res?.data;
      if (res?.status === 200 && Array.isArray(data)) {
        const mapped = data
          .map((r) => {
            const pr = mapCatalogPortalRoleFromApi(r);
            if (!pr) return null;
            return {
              roleId: pr.roleId,
              name: pr.name,
              id: pr.id,
            };
          })
          .filter(Boolean);
        commit('SET_ROLES', mapped);
        return mapped;
      }
    } catch (e) {
      // ignore
    }
    commit('SET_ROLES', []);
    return [];
  },

  async fetchUsers({ commit, state, rootState }) {
    commit('SET_LOADING', true);
    try {
      const legalEntityId = getEntityId();
      if (legalEntityId) {
        if (!hasPortalRight(rootState, B2bPortalRight.UsersView)) {
          commit('SET_USERS', []);
          return [];
        }
        try {
          const res = await axios.get(`/api/users`);
          const data = res?.data;
          if (res?.status === 200 && Array.isArray(data)) {
            const mapped = data.map((p) => {
              const rawList = p.b2bPortalRoles || p.B2bPortalRoles || p.roles || p.Roles || [];
              const b2bPortalRoles = rawList
                .map((r) => mapUserPortalRoleFromApi(r))
                .filter(Boolean);
              const dob = p.dateOfBirth ?? p.DateOfBirth;
              const dobStr =
                dob == null || dob === ''
                  ? ''
                  : typeof dob === 'string'
                    ? dob.slice(0, 10)
                    : '';
              return {
                id: p.personId ,
                firstName: p.firstName || '',
                lastName: p.surName || '',
                email: p.email || p.Email || '',
                phone: p.phone || p.Phone || '',
                dateOfBirth: dobStr,
                b2bPortalRoles,
                roleIds: b2bPortalRoles.map((x) => x.roleId),
                roles: b2bPortalRoles.map((x) => x.name).filter(Boolean),
                createdOnPortal: !!(p.createdOnPortal ?? p.CreatedOnPortal),
              };
            });
            commit('SET_USERS', mapped);
            return mapped;
          }
        } catch (e) {
          // fall through to local fake list
        }
      }

      commit('SET_USERS', state.users);
      return state.users;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async addUser({ commit, dispatch, state, rootState }, payload) {
    const p = payload || {};
    const id = p.id || `u-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const roleIds = Array.isArray(p.roleIds)
      ? p.roleIds.map((x) => Number(x)).filter((x) => Number.isFinite(x))
      : [];
    const roleAssignmentIds = p.roleAssignmentIds && typeof p.roleAssignmentIds === 'object' ? p.roleAssignmentIds : {};
    const b2bPortalRoles = buildB2bPortalRolesForRequest(roleIds, roleAssignmentIds);
    const roles = roleIds.length
      ? (state.roles || []).filter((r) => roleIds.includes(Number(r.roleId))).map((r) => r.name).filter(Boolean)
      : (
        Array.isArray(p.roles)
          ? p.roles
          : String(p.roles || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
      );

    const user = {
      id,
      firstName: String(p.firstName || '').trim(),
      lastName: String(p.lastName || '').trim(),
      email: String(p.email || '').trim(),
      dateOfBirth: String(p.dateOfBirth || '').trim(),
      phone: String(p.phone || '').trim(),
      roles,
      roleIds,
      createdOnPortal: true,
      b2bPortalRoles: b2bPortalRoles.map((x) => ({
        id: x.id,
        roleId: x.roleId,
        name: (state.roles || []).find((r) => Number(r.roleId) === x.roleId)?.name || '',
      })),
    };

    const legalEntityId = getEntityId();
    if (legalEntityId) {
      try {
        denyUnlessPortalRight(rootState, B2bPortalRight.UsersAdd, 'add user');
        const dto = {
          firstName: user.firstName,
          surName: user.lastName,
          email: user.email,
          dateOfBirth: user.dateOfBirth || null,
          phone: user.phone.replace(/[^\d]/g, ''),
          b2bPortalRoles: buildB2bPortalRolesForRequest(roleIds, roleAssignmentIds),
          addresses: [],
        };

        const res = await axios.post(`/api/users`, dto);
        if (res?.status === 200 && (res?.data?.success === true || res?.data?.Success === true)) {
          // refresh list to pick up server-generated PersonId + role IDs
          try { await dispatch('fetchUsers'); } catch {}
          return user;
        }
        throw new Error('Failed to add user');
      } catch (e) {
        const status = e?.response?.status;
        if (status === 400 || status === 422) {
          const data = e?.response?.data;
          const stringify = (v) => {
            if (typeof v === 'string') return v;
            if (v && typeof v === 'object' && v.message) return v.message;
            if (v && typeof v === 'object') return JSON.stringify(v);
            return String(v);
          };
          const fromErrorsObject = (obj) => {
            if (!obj || typeof obj !== 'object') return '';
            if (Array.isArray(obj)) return obj.map(stringify).filter(Boolean).join('. ');
            const entries = Object.entries(obj);
            const parts = [];
            for (const [key, val] of entries) {
              const arr = Array.isArray(val) ? val : (val ? [val] : []);
              const joined = arr.map(stringify).filter(Boolean).join(' ');
              if (!joined) continue;
              parts.push(key ? `${key}: ${joined}` : joined);
            }
            return parts.join(' | ');
          };

          const msg =
            data?.errorMessage
            || data?.ErrorMessage
            || data?.message
            || data?.Message
            || fromErrorsObject(data?.errors)
            || fromErrorsObject(data)
            || 'Validation error';
          throw new Error(msg);
        }
        // For any other failure (including 500), do NOT fall back to local add.
        const msg = e?.response?.data?.message || e?.response?.data?.Message || e?.message || 'Failed to add user';
        throw new Error(msg);
      }
    }

    await new Promise((r) => setTimeout(r, 150));
    commit('ADD_USER', user);
    return user;
  },

  async updateUser({ commit, dispatch, state, rootState }, payload) {
    const p = payload || {};
    const personId = p.personId || p.id;
    if (!personId) {
      throw new Error('Missing user id');
    }
    const roleIds = Array.isArray(p.roleIds)
      ? p.roleIds.map((x) => Number(x)).filter((x) => Number.isFinite(x))
      : [];
    const roleAssignmentIds = p.roleAssignmentIds && typeof p.roleAssignmentIds === 'object' ? p.roleAssignmentIds : {};
    const b2bPortalRoles = buildB2bPortalRolesForRequest(roleIds, roleAssignmentIds);
    const roles = roleIds.length
      ? (state.roles || []).filter((r) => roleIds.includes(Number(r.roleId))).map((r) => r.name).filter(Boolean)
      : [];

    const userPatch = {
      id: String(personId),
      firstName: String(p.firstName || '').trim(),
      lastName: String(p.lastName || '').trim(),
      email: String(p.email || '').trim(),
      dateOfBirth: String(p.dateOfBirth || '').trim(),
      phone: String(p.phone || '').trim().replace(/[^\d]/g, ''),
      roles,
      roleIds,
      b2bPortalRoles: b2bPortalRoles.map((x) => ({
        id: x.id,
        roleId: x.roleId,
        name: (state.roles || []).find((r) => Number(r.roleId) === x.roleId)?.name || '',
      })),
    };

    const legalEntityId = getEntityId();
    if (legalEntityId) {
      try {
        denyUnlessPortalRight(rootState, B2bPortalRight.UsersView, 'update user');
        const dto = {
          firstName: userPatch.firstName,
          surName: userPatch.lastName,
          email: userPatch.email,
          dateOfBirth: userPatch.dateOfBirth || null,
          phone: userPatch.phone,
          b2bPortalRoles: buildB2bPortalRolesForRequest(roleIds, roleAssignmentIds),
          addresses: [],
        };

        const res = await axios.put(`/api/users/${personId}`, dto);
        if (res?.status === 200 && (res?.data?.success === true || res?.data?.Success === true)) {
          try {
            await dispatch('fetchUsers');
          } catch {}
          return userPatch;
        }
        throw new Error('Failed to update user');
      } catch (e) {
        const status = e?.response?.status;
        if (status === 400 || status === 422) {
          const data = e?.response?.data;
          const fromErrorsObject = (obj) => {
            if (!obj || typeof obj !== 'object') return '';
            const entries = Object.entries(obj);
            const parts = [];
            for (const [key, val] of entries) {
              const arr = Array.isArray(val) ? val : (val ? [val] : []);
              const joined = arr.map((v) => String(v)).filter(Boolean).join(' ');
              if (!joined) continue;
              parts.push(key ? `${key}: ${joined}` : joined);
            }
            return parts.join(' | ');
          };

          const msg =
            data?.errorMessage
            || data?.ErrorMessage
            || data?.message
            || data?.Message
            || fromErrorsObject(data?.errors)
            || fromErrorsObject(data)
            || 'Validation error';
          throw new Error(msg);
        }
        const msg = e?.response?.data?.message || e?.response?.data?.Message || e?.message || 'Failed to update user';
        throw new Error(msg);
      }
    }

    await new Promise((r) => setTimeout(r, 100));
    commit('UPDATE_USER', userPatch);
    return userPatch;
  },

  async deleteUser({ commit, dispatch, rootState }, userId) {
    if (!userId) {
      throw new Error("Missing user id");
    }
    const legalEntityId = getEntityId();
    if (legalEntityId) {
      try {
        denyUnlessPortalRight(rootState, B2bPortalRight.UsersView, 'delete user');
        const res = await axios.delete(`/api/users/${userId}`);
        if (res?.status === 200) {
          try {
            await dispatch("fetchUsers");
          } catch {}
          return true;
        }
        throw new Error("Failed to delete user");
      } catch (e) {
        const status = e?.response?.status;
        if (status === 400 || status === 422) {
          const data = e?.response?.data;
          const fromErrorsObject = (obj) => {
            if (!obj || typeof obj !== "object") return "";
            const entries = Object.entries(obj);
            const parts = [];
            for (const [key, val] of entries) {
              const arr = Array.isArray(val) ? val : val ? [val] : [];
              const joined = arr.map((v) => String(v)).filter(Boolean).join(" ");
              if (!joined) continue;
              parts.push(key ? `${key}: ${joined}` : joined);
            }
            return parts.join(" | ");
          };

          const msg =
            data?.errorMessage
            || data?.ErrorMessage
            || data?.message
            || data?.Message
            || fromErrorsObject(data?.errors)
            || fromErrorsObject(data)
            || "Validation error";
          throw new Error(msg);
        }
        const msg =
          e?.response?.data?.message
          || e?.response?.data?.Message
          || e?.message
          || "Failed to delete user";
        throw new Error(msg);
      }
    }

    await new Promise((r) => setTimeout(r, 100));
    commit("DELETE_USER", userId);
    return true;
  },
};

const getters = {
  users: (state) => state.users || [],
  roles: (state) => state.roles || [],
  isLoading: (state) => !!state.isLoading,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

